import React, { useEffect, useState } from 'react';
import { toPersianDigits } from '@/utils/persianUtils';

interface ArbitrageProfitDisplayProps {
    profit: string | null;
    direction: 'up' | 'down' | 'none';
    /** 'strip': full-width band on top of the table (phones); 'inline': compact box beside the tabs */
    variant?: 'strip' | 'inline';
}

/**
 * Strip on top of the exchange table (TetherChand pattern): what you would gain buying at the cheapest
 * exchange and selling at the dearest one, before fees. Hidden until the socket sends a value.
 */
const ArbitrageProfitDisplay: React.FC<ArbitrageProfitDisplayProps> = ({
    profit,
    direction,
    variant = 'strip',
}) => {
    // Short colour flash when the value moves
    const [flashColor, setFlashColor] = useState<'up' | 'down' | null>(null);

    useEffect(() => {
        if (direction === 'none') return;
        setFlashColor(direction);
        const timer = setTimeout(() => setFlashColor(null), 1200);
        return () => clearTimeout(timer);
    }, [profit, direction]);

    if (!profit || !Number.isFinite(Number(profit))) return null;

    const value = toPersianDigits(Number(profit).toFixed(2)).replace('.', '٫');
    const pill =
        flashColor === 'down'
            ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
            : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';

    const box =
        variant === 'inline'
            ? 'flex items-center gap-4 rounded-xl border border-emerald-500/20 px-3 py-1.5 bg-emerald-500/[0.06] dark:bg-emerald-500/[0.08]'
            : 'flex items-center justify-between gap-3 min-h-11 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-white/10 bg-emerald-500/[0.06] dark:bg-emerald-500/[0.08]';

    return (
        <div
            className={box}
            title='اختلاف ارزان‌ترین قیمت خرید و گران‌ترین قیمت فروش بین صرافی‌های این جدول، بدون کارمزد و هزینهٔ انتقال'
        >
            <span className='flex items-center gap-2 min-w-0'>
                <span className='flex size-6 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'>
                    <svg
                        aria-hidden='true'
                        className='size-3.5'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2.5'
                        viewBox='0 0 24 24'
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.94'
                        />
                    </svg>
                </span>
                <span className='flex flex-col min-w-0'>
                    <span className='truncate text-xs font-bold text-gray-800 dark:text-gray-100'>
                        سود آربیتراژ تتر
                    </span>
                    <span className='truncate text-[10px] text-gray-500 dark:text-gray-400'>
                        خرید ارزان‌ترین، فروش گران‌ترین · بدون کارمزد
                    </span>
                </span>
            </span>
            <span className='flex shrink-0 items-center gap-1.5'>
                <span className='relative flex size-2'>
                    <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-75' />
                    <span className='relative inline-flex size-2 rounded-full bg-emerald-600' />
                </span>
                <span
                    className={`inline-flex h-7 items-center rounded-full px-3 text-sm font-black tabular-nums transition-colors duration-500 ${pill}`}
                    dir='ltr'
                >
                    {value}٪
                </span>
            </span>
        </div>
    );
};

export default ArbitrageProfitDisplay;
